// src/utils/locationSqftDerived.js
//
// LSE-SQFT-SINGLE-SOURCE — canonical sqft derivation from leases.
//
// Formula:
//   • Occupant-type leases (direct_lease, sublease_inbound, serviced_office)
//     ADD their size to the location's net sqft.
//   • Outbound leases (sublease_outbound) SUBTRACT their size.
//   • Active, holdover and draft non-deleted leases retain the existing scope.
//     Complete child areas resolve before unit-normalized legacy parent size.
//   • Canonical derivation preserves unresolved recorded-area nulls.
//     Offices display aggregates per-lease display fallbacks separately.
//   • Negative sums are clamped to 0 (can occur if sublease-out > in).
//
// Call-site guide:
//   • All UI sqft reads must go through getDisplayedSqft(location, null).
//   • Detail payloads carry location.leases (LEASE_CHILD_COLS includes `size`
//     since LSE-SQFT-SINGLE-SOURCE) — getDisplayedSqft derives fresh from
//     that array automatically.
//   • List payloads carry canonical derivedSqft and a dated display basis.
//
// Exported API
// ------------
//   SQFT_OCCUPANT_TYPES  Set<string>    lease types that ADD to net sqft
//   SQFT_OUTBOUND_TYPE   string         lease type that SUBTRACTS from net sqft
//   isActiveLease(l)     boolean        true when lease is active and not soft-deleted
//   deriveLocationSqft(leases)          number | null
//   getDisplayedSqft(location, _)       number | null
//   isServicedNoArea(loc)               boolean — true for serviced offices with no displayable sqft

import { canonicalLeaseFootprint, currentLeaseFootprint, validFootage } from "../../shared/leaseAreaBasis.js";

export const SQFT_OCCUPANT_TYPES = new Set([
  "direct_lease",
  "sublease_inbound",
  "serviced_office",
]);

export const SQFT_OUTBOUND_TYPE = "sublease_outbound";

// Returns true when a lease is in a qualifying status (active, holdover, or
// draft) and has not been soft-deleted.
// LSE-DRAFT-LEASES-COUNT-IN-DERIVED-METRICS: draft and holdover leases now
// contribute to net sqft alongside active leases. Terminated, expired, and
// in_negotiation remain excluded — only these three statuses are authorised
// to add/subtract from the derived footprint.
export function isActiveLease(lease) {
  return (
    lease != null &&
    (lease.status === "active" ||
      lease.status === "holdover" ||
      lease.status === "draft") &&
    !lease.deletedAt
  );
}

// Derive a location's net sqft from an array of lease objects.
// Returns a non-negative Number, or null when there are no active leases
// with a non-null size (callers should fall back to stored location.sqft).
export function deriveLocationSqft(leases, asOf) {
  if (!Array.isArray(leases) || leases.length === 0) return null;

  const active = leases.filter(isActiveLease);
  if (active.length === 0) return null;

  // Only active leases that have a concrete, finite size contribute.
  // If none do, we cannot derive a value — return null so the caller
  // falls back to the stored location.sqft.
  const resolved = active.filter((l) => SQFT_OCCUPANT_TYPES.has(l.type) || l.type === SQFT_OUTBOUND_TYPE)
    .map((lease) => ({ lease, basis: canonicalLeaseFootprint(lease, asOf) }));
  if (resolved.some(({ basis }) => basis.source === "current_lease_areas" && basis.grossSf == null)) return null;
  const withSize = resolved.filter(({ basis }) => validFootage(basis.grossSf) != null);
  if (withSize.length === 0) return null;

  let sum = 0;
  for (const { lease, basis } of withSize) {
    const size = Number(basis.grossSf);
    if (SQFT_OCCUPANT_TYPES.has(lease.type)) {
      sum += size;
    } else if (lease.type === SQFT_OUTBOUND_TYPE) {
      sum -= size;
    }
  }
  return Math.max(0, sum);
}

// Offices presentation only; do not substitute this for canonical measurements
// in density, annual Financials or unrelated analytical SQL reads. Unknown
// leases are omitted, not counted as measured zero and not allowed to erase
// other usable leases. Counts expose any incomplete aggregation.
export function resolvedOfficeFootprint(leases, asOf) {
  const qualifying = (Array.isArray(leases) ? leases : []).filter((lease) =>
    isActiveLease(lease) && (SQFT_OCCUPANT_TYPES.has(lease.type) || lease.type === SQFT_OUTBOUND_TYPE));
  const resolved = qualifying.map((lease) => ({ lease, basis: currentLeaseFootprint(lease, asOf) }));
  const usable = resolved.filter(({ basis }) => validFootage(basis.grossSf) != null);
  const grossSf = usable.length ? Math.max(0, usable.reduce((sum, { lease, basis }) =>
    sum + (lease.type === SQFT_OUTBOUND_TYPE ? -basis.grossSf : basis.grossSf), 0)) : null;
  return {
    grossSf, derivedGrossSf: deriveLocationSqft(qualifying, asOf),
    source: grossSf == null ? "unavailable" : "signed_resolved_lease_footprint",
    canonicalSource: "signed_current_lease_footprint",
    fallbackUsed: usable.some(({ basis }) => basis.source === "legacy_lease_size"),
    resolvedLeaseCount: usable.length, unresolvedLeaseCount: resolved.length - usable.length,
    asOf: asOf ?? new Date().toISOString().slice(0, 10),
  };
}

// Frozen pre-display-fix read contract for analytical/financial consumers.
// An Offices presentation correction is not authority to change their basis.
export function getAnalyticalSqft(location) {
  if (!location) return null;
  const projected = validFootage(location.derivedSqft);
  const derived = projected ?? (Array.isArray(location.leases)
    ? deriveLocationSqft(location.leases, location.footprintAsOf) : null);
  return derived ?? validFootage(location.sqft);
}

// Return the sqft value to display for a location.
//
// Priority:
//   1. Explicit Offices displayedAreaBasis (including unavailable).
//   2. Existing canonical derivedSqft projection for compatible older reads.
//   3. Embedded lease display aggregation, retaining stored historical space.
//   4. Legacy payloads without either projection or leases retain stored sqft.
//
// The second parameter (_allLeases) is reserved for a future portfolio-
// level derivation pass and is currently unused. Pass null.
export function getDisplayedSqft(location, _allLeases) {
  if (!location) return null;
  // An explicit Offices display projection is authoritative even when null:
  // stored operational sqft must not manufacture missing lease footage.
  if (location.displayedAreaBasis?.canonicalSource === "signed_current_lease_footprint") {
    return validFootage(location.displayedAreaBasis.grossSf);
  }

  // Priority 1: LATERAL derived_sqft present on list-payload objects.
  // 0 is a valid derived value (clamped); only skip when truly null/undefined.
  if (validFootage(location.derivedSqft) != null) {
    return Number(location.derivedSqft);
  }

  if (Array.isArray(location.leases)) {
    return displayedOfficeFootprint(location).grossSf;
  }

  // Priority 3: stored column — populated by backfill migration; accurate for
  // locations with no qualifying active leases.
  return validFootage(location.sqft);
}

export function displayedOfficeFootprint(location) {
  if (location?.displayedAreaBasis?.canonicalSource === "signed_current_lease_footprint") {
    return location.displayedAreaBasis;
  }
  if (Array.isArray(location?.leases)) {
    const basis = resolvedOfficeFootprint(location.leases, location.footprintAsOf);
    // Retained historical space is separate from today's operational totals.
    const historical = ["vacated", "closed", "retired"].includes(location.stage);
    const stored = validFootage(location.sqft);
    return historical && basis.grossSf == null && stored != null
      ? { ...basis, grossSf: stored, source: "stored_office_sqft", fallbackUsed: true } : basis;
  }
  const projected = validFootage(location?.derivedSqft);
  const derived = projected;
  const grossSf = derived ?? validFootage(location?.sqft);
  return { grossSf, derivedGrossSf: derived, source: derived != null
    ? "signed_current_lease_footprint" : grossSf != null ? "stored_office_sqft" : "unavailable",
    fallbackUsed: derived == null && grossSf != null,
    asOf: location?.footprintAsOf ?? new Date().toISOString().slice(0, 10) };
}

// Returns true when a location has an active serviced_office lease AND has no
// displayable sqft.  When sqft is known (derivedSqft or stored sqft is non-null),
// this returns false so the numeric value is shown instead of "n/a · serviced".
//
// Works on both list payloads (loc.activeLeaseTypes array from JSONB) and
// detail payloads (loc.leases array with per-lease type field).
export function isServicedNoArea(loc) {
  if (!loc) return false;

  // If there is a displayable sqft value, the numeric figure should be shown —
  // "n/a" is only correct when sqft is genuinely absent.
  if (getDisplayedSqft(loc, null) != null) return false;
  // An unmeasured recorded suite is unknown, not evidence that serviced area
  // is inapplicable. Preserve the existing N/A label only for absent records.
  if (loc.footprintHasAreaRecords || (Array.isArray(loc.leases) && loc.leases.some((lease) =>
    isActiveLease(lease) && canonicalLeaseFootprint(lease).source === "current_lease_areas"))) return false;

  // List payload: activeLeaseTypes is a JSONB array aggregated by the server.
  if (Array.isArray(loc.activeLeaseTypes) && loc.activeLeaseTypes.includes("serviced_office")) {
    return true;
  }

  // Detail payload: check the embedded leases array for an active serviced_office.
  if (Array.isArray(loc.leases)) {
    return loc.leases.some((l) => isActiveLease(l) && l.type === "serviced_office");
  }

  return false;
}
